import { type IssueAssociation } from "@overmap-ai/common"
import { selectIssueAssociationById } from "@overmap-ai/core"
import { type ComponentProps, type ComponentPropsWithRef, memo, useCallback } from "react"

import { useConfirmDeleteDialog } from "../../hooks/utils"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { IssueAssociationAssetListItem } from "./IssueAssociationAssetListItem"
import { IssueAssociationIssueListItem } from "./IssueAssociationIssueListItem"

interface IssueAssociationListItemProps extends ComponentPropsWithRef<"div"> {
	issueAssociationId: IssueAssociation["uuid"]
	type: "asset" | "issue"
	size?: ComponentProps<typeof IssueAssociationIssueListItem>["size"]
}

export const IssueAssociationListItem = memo<IssueAssociationListItemProps>((props) => {
	const { issueAssociationId, type, ...rest } = props

	const issueAssociation = useAppSelector(selectIssueAssociationById(issueAssociationId))

	if (!issueAssociation) {
		throw new Error(`Issue association with id ${issueAssociationId} not found in store.`)
	}

	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete issue association",
			description: "The issue association will be deleted immediately. This action cannot be undone.",
			onAction: () => sdk.issueAssociations.delete(issueAssociation.uuid),
		})
	}, [issueAssociation.uuid, openConfirmDeleteDialog])

	return type === "issue" ? (
		<IssueAssociationIssueListItem issueId={issueAssociation.issue} onDelete={handleDelete} size="sm" {...rest} />
	) : (
		<IssueAssociationAssetListItem assetId={issueAssociation.asset} onDelete={handleDelete} size="sm" {...rest} />
	)
})
IssueAssociationListItem.displayName = "IssueAssociationListItem"
